import React, { useState, useEffect } from 'react';
import { DataService } from '../services/dataService';
import { SiteConfig } from '../types';
import { Copy, Check, Heart, ShieldCheck } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const Donate: React.FC = () => {
  const [config, setConfig] = useState<SiteConfig>(DataService.getConfig());
  const [amount, setAmount] = useState<string>('501');
  const [copied, setCopied] = useState(false);
  const { language, t } = useLanguage();

  useEffect(() => {
    setConfig(DataService.getConfig());
    window.scrollTo(0, 0);
  }, []);

  const handleCopy = () => {
    navigator.clipboard.writeText(config.upiId);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const predefinedAmounts = ['101', '201', '501', '1001', '5001'];

  // Generate UPI URL
  const pa = (config.upiId || '').trim();
  const amtNum = parseFloat(String(amount || '0'));
  const amt = isNaN(amtNum) ? '0' : String(Math.max(1, Math.floor(amtNum)));
  const pn = encodeURIComponent((config.templeName || '').trim());
  const tr = `TXN${Date.now()}`;
  const upiLink = `upi://pay?pa=${pa}&am=${amt}&cu=INR`;
  
  // Use a public QR code API (QR Server) to generate the image
  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(upiLink)}`;
  const gpayIntent = '';
  const phonepeIntent = '';
  const paytmIntent = '';

  const openUPIApp = (link: string) => {
    try {
      window.location.href = link;
    } catch {
      window.open(link, '_self');
    }
  };

  return (
    <div className="relative py-12 px-4 bg-gray-50 min-h-screen">
      <img
         src="/images/t5.jpeg"
        alt=""
        onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
        className="absolute inset-0 w-full h-full object-cover opacity-10 pointer-events-none"
      />
      <div className="relative container mx-auto max-w-4xl z-10">
        <div className="text-center mb-12">
          <span className="bg-orange-100 text-orange-700 px-4 py-1 rounded-full text-sm font-bold uppercase tracking-wide mb-4 inline-block">
            {t('A handful of soil for Goddess & Development Fund', 'ഭഗവതിക്കായ്‌ ഒരു പിടി മണ്ണ് & വികസന ഫണ്ട്')}
          </span>
          <h1 className="text-4xl font-serif font-bold text-temple-orange-900 mb-4">{t('Make a Donation', 'സംഭാവന നൽകുക')}</h1>
          <p className="text-gray-600 max-w-2xl mx-auto text-lg">
            {t(
              'Your generous contributions will support the Bhagwati with a handful of soil endeavor and the ongoing development of the temple infrastructure.',
              'നിങ്ങളുടെ ഉദാരമായ സംഭാവനകൾ ഭഗവതിക്കായ്‌ ഒരു പിടി മണ്ണ്, ക്ഷേത്ര വികസന പ്രവർത്തനങ്ങൾ എന്നിവയ്ക്ക് സഹായകമാകും.'
            )}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 items-start">
          {/* Donation Controls */}
          <div className="bg-white/90 p-8 rounded-2xl shadow-md border border-gray-100">
            <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
              <Heart className="text-temple-orange-600 fill-current" size={20} />
              {t('Choose Amount', 'തുക തിരഞ്ഞെടുക്കുക')}
            </h2>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('Select Amount (INR)', 'തുക (രൂപ)')}</label>
              <div className="flex flex-wrap gap-3 mb-4">
                {predefinedAmounts.map((amt) => (
                  <button
                    key={amt}
                    onClick={() => setAmount(amt)}
                    className={`px-4 py-2 rounded-lg font-bold transition-all border ${
                      amount === amt 
                        ? 'bg-temple-orange-600 text-white border-temple-orange-600 shadow-lg scale-105' 
                        : 'bg-white text-gray-600 border-gray-200 hover:border-orange-300 hover:bg-orange-50'
                    }`}
                  >
                    ₹{amt}
                  </button>
                ))}
              </div>
              
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('Or Enter Custom Amount', 'അല്ലെങ്കിൽ മറ്റൊരു തുക നൽകുക')}</label>
              <div className="relative">
                <span className="absolute left-4 top-3.5 text-gray-500 font-bold">₹</span>
                <input 
                  type="number" 
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="w-full pl-8 pr-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-temple-orange-500 focus:border-transparent outline-none text-lg font-bold text-gray-800"
                  placeholder="Enter amount"
                />
              </div>
            </div>

            <div className="bg-blue-50 p-4 rounded-xl border border-blue-100">
              <h3 className="text-sm font-bold text-blue-800 mb-1 flex items-center gap-2">
                <ShieldCheck size={16} /> {t('Bank Transfer Details', 'ബാങ്ക് അക്കൗണ്ട് വിവരങ്ങൾ')}
              </h3>
              <p className="text-xs text-blue-600 mb-2">{t('For large donations (NEFT/RTGS)', 'വലിയ തുകകൾക്ക് (NEFT/RTGS)')}</p>
              <div className="text-sm text-gray-700 space-y-1">
                <p><span className="font-semibold">Account Name:</span> {config.templeName}</p>
                <p><span className="font-semibold">Bank:</span> State Bank of India, Arunoottimangalam</p>
                <p><span className="font-semibold">IFSC:</span> SBIN0008595</p>
                <p><span className="font-semibold">Account No:</span> 10548552943</p>
              </div>
            </div>
          </div>

          {/* QR Code Section */}
          <div className="bg-white/90 p-8 rounded-2xl shadow-md border border-gray-100 flex flex-col items-center text-center">
            <h2 className="text-xl font-bold text-gray-800 mb-2">{t('Scan & Pay', 'സ്കാൻ ചെയ്ത് പണമടയ്ക്കുക')}</h2>
            <p className="text-gray-500 text-sm mb-6">{t('Use GPay, PhonePe, Paytm, or any UPI app', 'GPay, PhonePe, Paytm അല്ലെങ്കിൽ മറ്റേതെങ്കിലും യുപിഐ ആപ്പ് ഉപയോഗിക്കാം')}</p>
            
            <div className="bg-white p-4 rounded-xl shadow-inner border border-gray-200 mb-6 relative">
              <img 
                src={qrCodeUrl} 
                alt="UPI QR Code" 
                className="w-48 h-48 md:w-64 md:h-64 object-contain"
                loading="lazy"
              />
            </div>

            <div className="w-full bg-gray-50 p-3 rounded-lg flex items-center justify-between border border-gray-200 mb-6">
              <div className="text-left overflow-hidden">
                <p className="text-xs text-gray-500 uppercase font-bold">UPI ID</p>
                <p className="text-sm font-medium text-gray-800 truncate">{config.upiId}</p>
              </div>
              <button 
                onClick={handleCopy}
                className="ml-2 p-2 hover:bg-gray-200 rounded-full transition-colors text-gray-600"
                title="Copy UPI ID"
              >
                {copied ? <Check size={18} className="text-green-600" /> : <Copy size={18} />}
              </button>
            </div>

            <button
              type="button"
              disabled
              className="block w-full bg-temple-orange-600 text-white py-3 rounded-xl font-bold mb-4 opacity-60 cursor-not-allowed"
            >
              Scan Using UPI App
            </button>

            <p className="text-xs text-gray-400">
                {t('After payment, please send the screenshot to our WhatsApp', 'പണമടച്ച ശേഷം സ്ക്രീൻഷോട്ട് വാട്ട്‌സ്ആപ്പിൽ അയക്കുക')} <span className="font-bold text-gray-600">{config.whatsappNumber}</span> {t('for receipt.', 'രസീതിനായി.')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Donate;
